export const backendApi = {
  auth: {
    register: (api, payload) => api.post('/auth/register', payload),
    requestEmailVerification: (api, payload) => api.post('/auth/request-email-verification', payload),
    verifyEmail: (api, payload) => api.post('/auth/verify-email', payload),
    requestPasswordReset: (api, payload) => api.post('/auth/request-password-reset', payload),
    resetPassword: (api, payload) => api.post('/auth/reset-password', payload),
    token: (api, payload) => api.post('/auth/token', payload),
    login: (api, payload) => api.post('/auth/login', payload),
    refresh: (api, params) => api.post('/auth/refresh', null, { params }),
    me: (api) => api.get('/auth/me'),
  },
  products: {
    create: (api, payload) => api.post('/products', payload),
    list: (api, params) => api.get('/products', { params }),
    categories: (api) => api.get('/products/categories'),
    lowStock: (api) => api.get('/products/low-stock'),
    update: (api, productId, payload) => api.put(`/products/${productId}`, payload),
    remove: (api, productId) => api.delete(`/products/${productId}`),
    restore: (api, productId) => api.post(`/products/${productId}/restore`),
  },
  customers: {
    create: (api, payload) => api.post('/customers', payload),
    list: (api, params) => api.get('/customers', { params }),
    getOne: (api, customerId) => api.get(`/customers/${customerId}`),
    update: (api, customerId, payload) => api.put(`/customers/${customerId}`, payload),
    remove: (api, customerId) => api.delete(`/customers/${customerId}`),
    restore: (api, customerId) => api.post(`/customers/${customerId}/restore`),
  },
  debts: {
    list: (api, params) => api.get('/debts', { params }),
    payment: (api, payload) => api.post('/debts/payment', payload),
  },
  sales: {
    create: (api, payload) => api.post('/sales', payload),
    list: (api, params) => api.get('/sales', { params }),
    getOne: (api, saleId) => api.get(`/sales/${saleId}`),
    addRefund: (api, saleId, payload) => api.post(`/sales/${saleId}/refunds`, payload),
    listRefunds: (api, saleId) => api.get(`/sales/${saleId}/refunds`),
    byReceipt: (api, receiptNumber) => api.get(`/sales/by-receipt/${receiptNumber}`),
  },
  expenses: {
    create: (api, payload) => api.post('/expenses', payload),
    list: (api, params) => api.get('/expenses', { params }),
    addPayment: (api, expenseId, payload) => api.post(`/expenses/${expenseId}/payments`, payload),
  },
  employees: {
    create: (api, payload) => api.post('/employees', payload),
    list: (api, params) => api.get('/employees', { params }),
    salesOptions: (api) => api.get('/employees/sales-options'),
    update: (api, employeeId, payload) => api.put(`/employees/${employeeId}`, payload),
    remove: (api, employeeId) => api.delete(`/employees/${employeeId}`),
  },
  settings: {
    saveBusiness: (api, payload) => api.post('/settings/business', payload),
    getBusiness: (api) => api.get('/settings/business'),
    getPermissions: (api) => api.get('/settings/permissions'),
    getMyPermissions: (api) => api.get('/settings/permissions/me'),
    updatePermissions: (api, payload) => api.put('/settings/permissions', payload),
    getSubscription: (api) => api.get('/settings/subscription'),
    updateSubscription: (api, payload) => api.put('/settings/subscription', payload),
    getBackup: (api) => api.get('/settings/backup'),
  },
  billing: {
    summary: (api) => api.get('/billing/summary'),
    createPayment: (api, payload) => api.post('/billing/payments', payload),
  },
  reports: {
    dailySales: (api, params) => api.get('/reports/daily-sales', { params }),
    sales: (api, params) => api.get('/reports/sales', { params }),
    salesExportCsv: (api, params) => api.get('/reports/sales/export/csv', { params }),
    salesExportPdf: (api, params) => api.get('/reports/sales/export/pdf', { params }),
    salesPdf: (api, params) => api.get('/reports/sales/pdf', { params }),
    salesCsvSummary: (api, params) => api.get('/reports/sales/csv/summary', { params }),
    salesCsvTransactions: (api, params) => api.get('/reports/sales/csv/transactions', { params }),
    salesCsvProducts: (api, params) => api.get('/reports/sales/csv/products', { params }),
    survivalMetrics: (api) => api.get('/reports/survival-metrics'),
    taxSummary: (api, params) => api.get('/reports/tax-summary', { params }),
  },
  users: {
    me: (api) => api.get('/users/me'),
    list: (api) => api.get('/users'),
    create: (api, payload) => api.post('/users', payload),
    updateRole: (api, userId, role) => api.put(`/users/${userId}/role`, null, { params: { role } }),
    remove: (api, userId) => api.delete(`/users/${userId}`),
    resetPassword: (api, userId, newPassword) =>
      api.post(`/users/${userId}/reset-password`, null, { params: { new_password: newPassword } }),
  },
  auditLogs: {
    list: (api, params) => api.get('/audit-logs', { params }),
    exportCsv: (api, params) => api.get('/audit-logs/export/csv', { params }),
  },
  inventory: {
    adjust: (api, payload) => api.post('/inventory/adjust', payload),
    adjustments: (api, params) => api.get('/inventory/adjustments', { params }),
    receive: (api, payload) => api.post('/inventory/receive', payload),
    getBatches: (api, { productId, includeDepleted = false } = {}) => {
      const params = new URLSearchParams();
      if (productId) {
        params.set('product_id', productId);
      }
      params.set('include_depleted', String(includeDepleted));
      return api.get(`/inventory/batches?${params.toString()}`);
    },
  },
  coupons: {
    list: (api) => api.get('/coupons'),
    create: (api, payload) => api.post('/coupons', payload),
    update: (api, couponId, payload) => api.put(`/coupons/${couponId}`, payload),
    remove: (api, couponId) => api.delete(`/coupons/${couponId}`),
    validate: (api, payload) => api.post('/coupons/validate', payload),
  },
  platform: {
    listBillingPayments: (api, params, headers) => api.get('/platform/billing/payments', { params, headers }),
    reviewBillingPayment: (api, paymentId, payload, headers) =>
      api.post(`/platform/billing/payments/${paymentId}/review`, payload, { headers }),
  },
  system: {
    seedData: (api) => api.post('/seed-data'),
  },
};

export default backendApi;
